import { Injectable } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { environment } from '@env/environment';
import { PageBaseService } from '@app/shared/services/page-base.service';
import { ExternalApplicationDTO } from '../external-application-view/models/external-application.model';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { tap, switchMap } from 'rxjs/operators';
import { Guid } from '@app/core/constants/constants';
import { HttpClient } from '@angular/common/http';

@Injectable({
    providedIn: 'root'
})
export class ExternalApplicationService extends PageBaseService<ExternalApplicationDTO.ExternalApplication> {
    public summary: Observable<ExternalApplicationDTO.ExternalApplicationSummary>;
    private simpleSourceSubject = new BehaviorSubject<ExternalApplicationDTO.ExternalApplicationId[] | null>(null);

    public simpleSource$: Observable<ExternalApplicationDTO.ExternalApplicationId[]> = this.simpleSourceSubject.asObservable().pipe(
        switchMap(simpleSource => {
            if (simpleSource === null) {
                return this.initSimpleSource().pipe(
                    switchMap(() => this.simpleSourceSubject.asObservable())
                );
            } else {
                return of(simpleSource);
            }
        })
    );

    constructor(
        private formBuilder: UntypedFormBuilder,
        private http: HttpClient
    ) {
        super(environment.apiv2, 'external-applications');

        this.form = this.formBuilder.group({
            header: this.formBuilder.group({}),
            edit: this.formBuilder.group({
                id: [],
                title: ['', [Validators.required, Validators.maxLength(64)]],
                subtitle: ['', [Validators.maxLength(128)]],
                description: ['', [Validators.maxLength(256)]],
                url: ['', [Validators.required, Validators.maxLength(256)]],
                icon: ['', [Validators.maxLength(64)]],
                menuTitle: ['', [Validators.required, Validators.maxLength(64)]],
                menuTitleColor: ['', [Validators.required, Validators.maxLength(32)]],
                menuSubtitle: ['', [Validators.required, Validators.maxLength(128)]],
                menuIcon: ['', [Validators.maxLength(64)]],
                menuIconColor: ['', [Validators.required, Validators.maxLength(32)]],
                displayMode: ['IFrame', [Validators.required]],
                menuType: ['Item', [Validators.required]],
                iframeStyle: ['', [Validators.maxLength(1024)]],
                parentId: [],
                order: [0, [Validators.required]],
                isPublic: [false, [Validators.required]],
                refreshInterval: [0, [Validators.required]]
            })
        });
    }

    private initSimpleSource(): Observable<void> {
        if (this.simpleSourceSubject.getValue() === null) {
            return this.getSimple<ExternalApplicationDTO.ExternalApplicationId[]>().pipe(
                tap(apps => this.simpleSourceSubject.next(apps)),
                switchMap(() => of(void 0))
            );
        }
        return of(void 0);
    }

    public initSummary(id: string | Guid): Observable<void> {
        this.summary = this.fetchSummary<ExternalApplicationDTO.ExternalApplicationSummary>(id);
        return this.summary.pipe(
            switchMap(() => of(void 0))
        );
    }

    fetchUrlMetadata(url: string): Observable<UrlMetadataResponse> {
        return this.http.get<UrlMetadataResponse>(`${this.url}/${this.endpoint}/url-metadata`, {
            params: { url }
        });
    }
}

interface UrlMetadataResponse {
    title: string;
    description: string;
    isAccessible: boolean;
} 